import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, LayoutGrid, Search, Star, ChevronRight, Landmark, Sparkles, Share2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../lib/ThemeContext';
import PushToggle from '../components/PushToggle';
import { getIcon } from '../lib/icons';
import CategoryIntroModal, { getSeenIntros } from '../components/CategoryIntroModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [articleOfDay, setArticleOfDay] = useState(null);
  const [introCategory, setIntroCategory] = useState(null);
  const [firstName, setFirstName] = useState(() => {
    try { return localStorage.getItem('mibeko_user_name') || ''; } catch { return ''; }
  });

  useEffect(() => {
    const onNameChange = () => {
      try { setFirstName(localStorage.getItem('mibeko_user_name') || ''); } catch {}
    };
    window.addEventListener('mibeko-name-changed', onNameChange);
    return () => window.removeEventListener('mibeko-name-changed', onNameChange);
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h <= 17) return 'Bon après-midi';
    return 'Bonsoir';
  })();

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(r => r.json())
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${API_URL}/api/article-of-the-day`)
      .then(r => r.ok ? r.json() : null)
      .then(setArticleOfDay)
      .catch(() => {});
  }, []);

  const handleShareArticle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!articleOfDay) return;
    const url = `${window.location.origin}/article/${articleOfDay.id}`;
    const text = `${articleOfDay.article_number} — ${articleOfDay.domain_label}\n\n${articleOfDay.summary}\n\nDecouvrir : ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Mibeko · ${articleOfDay.article_number}`, text, url });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Article copie dans le presse-papier !');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('[HomePage] share/copy failed:', err?.message || err);
      }
    }
  };

  const totalTexts = categories.reduce((sum, c) => sum + (c.total_texts || 0), 0);
  const totalCategories = categories.length;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryClick = (e, cat) => {
    // Premiere fois : pop-up d'explication simple avec bouton Acceder
    if (cat.intro && !getSeenIntros()[cat.id]) {
      e.preventDefault();
      setIntroCategory(cat);
    }
  };

  return (
    <div className="page-container" style={{ paddingTop: '24px' }}>
      {introCategory && (
        <CategoryIntroModal category={introCategory} onClose={() => setIntroCategory(null)} />
      )}
      {/* Greeting */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '4px' }}
              data-testid="home-greeting">
            {greeting}{firstName ? ` ${firstName}` : ''},
          </h1>
          <p style={{ fontSize: '15px', color: 'var(--text-gray)' }}>
            Que recherchez-vous aujourd'hui ?
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <PushToggle />
          <button
            type="button"
            onClick={toggleTheme}
            className="icon-circle theme-toggle-btn"
            style={{ width: '44px', height: '44px', border: 'none', cursor: 'pointer' }}
            data-testid="theme-toggle-btn"
            aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
            title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            {theme === 'dark' ? (<Sun style={{ width: '20px', height: '20px' }} />) : (<Moon style={{ width: '20px', height: '20px' }} />)}
          </button>
          <Link to="/a-propos" className="icon-circle" style={{ width: '44px', height: '44px', textDecoration: 'none', color: 'inherit' }} data-testid="home-about-btn" aria-label="A propos">
            <Landmark style={{ width: '20px', height: '20px' }} />
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <div className="stat-card" data-testid="stat-textes">
          <div className="icon-circle" style={{ width: '40px', height: '40px' }}>
            <FileText style={{ width: '18px', height: '18px' }} />
          </div>
          <div>
            <div className="stat-number">{totalTexts}</div>
            <div className="stat-label">Textes de loi</div>
          </div>
        </div>
        <div
          className="stat-card"
          data-testid="stat-categories"
          onClick={() => navigate('/categories')}
          style={{ cursor: 'pointer' }}
        >
          <div className="icon-circle" style={{ width: '40px', height: '40px' }}>
            <LayoutGrid style={{ width: '18px', height: '18px' }} />
          </div>
          <div>
            <div className="stat-number">{totalCategories}</div>
            <div className="stat-label">Categories</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch}>
        <div className="search-bar" data-testid="home-search">
          <Search />
          <input
            type="text"
            placeholder="Rechercher une loi, un article..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </form>

      {/* Article du Jour */}
      {articleOfDay && (
        <Link
          to={`/article/${articleOfDay.id}`}
          className="article-of-day-card"
          data-testid="article-of-day-card"
          style={{ textDecoration: 'none' }}
        >
          <div className="aod-header">
            <div className="aod-badge">
              <Sparkles style={{ width: '14px', height: '14px' }} />
              <span>Article du jour</span>
            </div>
            <button
              type="button"
              onClick={handleShareArticle}
              className="aod-share-btn"
              aria-label="Partager l'article du jour"
              data-testid="aod-share-btn"
            >
              <Share2 style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
          <div className="aod-meta" data-testid="aod-meta">
            {articleOfDay.article_number} · {articleOfDay.domain_label}
          </div>
          <p className="aod-summary" data-testid="aod-summary">
            {articleOfDay.summary || articleOfDay.text_integral.slice(0, 220) + '...'}
          </p>
          <div className="aod-cta">
            Lire l'article complet
            <ChevronRight style={{ width: '16px', height: '16px' }} />
          </div>
        </Link>
      )}

      {/* Categories */}
      <div className="section-header">
        <h2>Categories Juridiques</h2>
        <Link to="/categories" data-testid="voir-tout-link">Voir tout</Link>
      </div>
      <div className="category-grid" style={{ marginBottom: '28px' }}>
        {categories.map(cat => {
          const Icon = getIcon(cat.icon);
          return (
            <Link
              key={cat.id}
              to={`/categorie/${cat.id}`}
              className="category-card"
              data-testid={`category-${cat.id}`}
              onClick={(e) => handleCategoryClick(e, cat)}
            >
              <div className="icon-circle">
                <Icon />
              </div>
              <div className="category-name">{cat.name}</div>
              <div className="category-count">{cat.total_texts} texte{cat.total_texts !== 1 ? 's' : ''}</div>
            </Link>
          );
        })}
      </div>

      {/* Quick Access */}
      <div className="section-header">
        <h2>Acces rapide</h2>
      </div>
      <Link to="/favoris" className="quick-access-card" data-testid="quick-favoris">
        <div className="icon-circle">
          <Star />
        </div>
        <div className="qa-text">
          <h3>Mes Favoris</h3>
          <p>Accedez rapidement a vos textes sauvegardes</p>
        </div>
        <ChevronRight style={{ width: '20px', height: '20px', color: 'var(--text-light)' }} />
      </Link>

      <div style={{ height: '20px' }} />
    </div>
  );
};

export default HomePage;
